import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { InquiryMaterialsPrompt } from './InquiryMaterialsPrompt';
import { AssignmentAlertPrompt } from './AssignmentAlertPrompt';
import { canStoreJob } from '../../lib/storeJobPermissions';

// Mounted above routes: creation inside CRM, quick scan or quotations uses the
// same durable prompt, which cannot disappear by navigating or refreshing.
export const InquiryMaterialsGate = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [pending, setPending] = useState(null);
  const sequence = useRef(0);
  const userId = user?.id;
  const isStore = user?.role === 'store_manager';
  const allowed = ['admin', 'manager'].includes(user?.role) || canStoreJob(user, 'assign');
  const refresh = useCallback(async () => {
    const request = ++sequence.current;
    if (!allowed || !userId) { setPending(null); return; }
    try {
      const { data } = isStore ? { data: {} } : await axios.get(`${API}/crm/inquiry-materials/pending`);
      // Customer sharing always takes precedence over staff notifications.
      if (data.inquiry) {
        if (request === sequence.current) setPending(data);
      } else {
        const staff = await axios.get(`${API}/assignment-alerts/pending`);
        if (request === sequence.current) setPending(staff.data.alert ? staff.data : null);
      }
    } catch (error) {
      console.error('Could not load required inquiry share', error);
    }
  }, [allowed, userId, isStore]);

  useEffect(() => {
    refresh();
    return () => { sequence.current += 1; };
  }, [refresh, location.pathname]);

  useEffect(() => {
    if (!allowed) return undefined;
    let timer;
    const interceptor = axios.interceptors.response.use(response => {
      const url = (response.config?.url || '').split('?')[0];
      const method = response.config?.method?.toLowerCase();
      const path = url.replace(API, '');
      if (['post', 'put', 'patch'].includes(method) && (
        path === '/crm/inquiries' || path === '/crm/quick-scan-inquiry'
        || (method === 'put' && /^\/crm\/inquiries\/[^/]+$/.test(path))
        || /^\/jobs\/[^/]+\/(assign|store-assignment|reassign-fabricator|sections)(\/[^/]+)?$/.test(path)
        || path === '/allocation/bulk-assign')) {
        // Let the creation form finish closing before mounting the required dialog.
        clearTimeout(timer);
        timer = setTimeout(refresh, 0);
      }
      return response;
    });
    window.addEventListener('focus', refresh);
    return () => {
      clearTimeout(timer);
      axios.interceptors.response.eject(interceptor);
      window.removeEventListener('focus', refresh);
    };
  }, [allowed, refresh]);

  if (!allowed || !pending) return null;
  if (pending.alert) return <AssignmentAlertPrompt key={pending.alert.id} alert={pending.alert}
    apiReady={pending.apiReady} onRefresh={refresh} onComplete={() => { setPending(null); refresh(); }} />;
  return <InquiryMaterialsPrompt key={pending.inquiry.id} inquiry={pending.inquiry}
    apiReady={pending.apiReady} initialShare={pending.preparedShare} onComplete={() => {
      setPending(null);
      window.dispatchEvent(new Event('inquiry-materials-shared'));
      refresh();
    }} />;
};